'use client';

import React, { useState } from 'react';
import type { BusinessInfo } from '../types';
import { authenticatedPost, authenticatedGet } from '../lib/api/authenticated-fetch';

interface BusinessInfoSettingsProps {
  apiBaseUrl?: string;
  onSave?: () => void;
}

export function BusinessInfoSettings({
  apiBaseUrl = '/api/meridian-v2',
  onSave,
}: BusinessInfoSettingsProps) {
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (key: string, value: string) => {
    setBusinessInfo((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const data = await authenticatedPost<any>(`${apiBaseUrl}/business-info`, {
        businessInfo,
      });
      if (data.success) {
        setSuccess(true);
        onSave?.();
      } else {
        setError(data.error || 'Failed to save business info');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save business info');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-md">
      <h2 className="text-xl font-semibold mb-4">Business Information</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Company Name</label>
          <input
            type="text"
            value={businessInfo.companyName || ''}
            onChange={(e) => handleChange('companyName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Industry</label>
          <input
            type="text"
            value={businessInfo.industry || ''}
            onChange={(e) => handleChange('industry', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Annual Revenue</label>
          <input
            type="number"
            value={businessInfo.annualRevenue || ''}
            onChange={(e) => handleChange('annualRevenue', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        {success && <p className="text-green-500 text-sm">Business info saved successfully!</p>}

        <button
          type="submit"
          className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  );
}

